// HeroSlider.jsx — Redesigned into a static, highly intuitive and engaging Hero section (removed carousel)

import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

// MUI Icons
import ArrowForwardIcon           from "@mui/icons-material/ArrowForward";
import FiberManualRecordIcon      from "@mui/icons-material/FiberManualRecord";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import LocalShippingOutlinedIcon  from "@mui/icons-material/LocalShippingOutlined";
import SupportAgentOutlinedIcon   from "@mui/icons-material/SupportAgentOutlined";
import CardGiftcardOutlinedIcon   from "@mui/icons-material/CardGiftcardOutlined";
import AutoAwesomeOutlinedIcon    from "@mui/icons-material/AutoAwesomeOutlined";

export default function HeroSlider() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-rose-600 via-pink-600 to-fuchsia-700 hero-glow py-16 lg:py-24 min-h-[500px] lg:min-h-[560px]">
      {/* Decorative background blurs */}
      <div className="absolute top-10 right-10 w-72 h-72 rounded-full blur-3xl opacity-30 bg-pink-400/30" />
      <div className="absolute top-1/2 left-1/4 w-96 h-96 rounded-full blur-3xl opacity-20 bg-emerald-400/20 -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full blur-3xl opacity-20 bg-amber-400/20" />

      {/* Grid Overlay for texture */}
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5 pointer-events-none mix-blend-overlay"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 h-full flex flex-col justify-center">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center w-full">

          {/* ── Text content ─────────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, ease: [0.77, 0, 0.175, 1] }}
            className="text-white text-center lg:text-left flex flex-col items-center lg:items-start"
          >
            {/* Badge */}
            <motion.span
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-md border border-white/30 text-white text-xs font-bold uppercase tracking-widest px-5 py-2 rounded-full mb-6 shadow-xl"
            >
              <AutoAwesomeOutlinedIcon sx={{ fontSize: 14 }} className="text-amber-300" />
              New Collection Has Arrived
            </motion.span>

            {/* Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold leading-tight mb-4 tracking-tight drop-shadow-sm"
            >
              Discover the <br className="hidden lg:block"/> Perfect Gift
            </motion.h1>
            
            {/* Subtext */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.45 }}
              className="text-white/90 text-base sm:text-lg md:text-xl lg:text-2xl mb-8 sm:mb-10 max-w-lg leading-relaxed font-light drop-shadow-sm"
            >
              Handpicked products for every taste and occasion. Make someone's day truly unforgettable.
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55 }}
              className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto"
            >
              <Link
                to="/products"
                className="group inline-flex w-full sm:w-auto justify-center items-center gap-2 bg-white text-rose-600 font-bold px-6 py-3 sm:px-8 sm:py-4 rounded-full hover:bg-gray-50 transition-all shadow-xl hover:shadow-2xl hover:-translate-y-1 text-base sm:text-lg"
              >
                Shop Now
                <ArrowForwardIcon sx={{ fontSize: 20 }} className="group-hover:translate-x-1.5 transition-transform" />
              </Link>
              <Link
                to="/?occasion=anniversary"
                className="inline-flex w-full sm:w-auto justify-center items-center gap-2 bg-transparent text-white font-semibold px-6 py-3 sm:px-8 sm:py-4 rounded-full hover:bg-white/10 transition-all border-2 border-white/30 text-base sm:text-lg backdrop-blur-sm"
              >
                Explore Occasions
              </Link>
            </motion.div>
            
            {/* Trust micro-badges */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="flex flex-wrap items-center justify-center lg:justify-start gap-4 sm:gap-6 mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-white/20 text-white/90 w-full"
            >
              <div className="flex items-center gap-2 text-sm font-semibold">
                <LocalShippingOutlinedIcon sx={{ fontSize: 22 }} className="text-amber-300 drop-shadow-sm" />
                Free Shipping $50+
              </div>
              <div className="flex items-center gap-2 text-sm font-semibold">
                <FavoriteBorderOutlinedIcon sx={{ fontSize: 22 }} className="text-amber-300 drop-shadow-sm" />
                Easy Returns
              </div>
              <div className="flex items-center gap-2 text-sm font-semibold">
                <SupportAgentOutlinedIcon sx={{ fontSize: 22 }} className="text-amber-300 drop-shadow-sm" />
                24/7 Support
              </div>
            </motion.div>
          </motion.div>

          {/* ── Illustration / Beautiful Composition ────────────────── */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, x: 20 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
            className="hidden lg:flex items-center justify-center relative h-full"
          >
            {/* Main Center Piece */}
            <div className="relative w-96 h-96 flex items-center justify-center">
              {/* Outer Glow */}
              <div className="absolute inset-0 rounded-full bg-white/10 blur-3xl scale-125 animate-pulse duration-1000" />
              
              {/* Sub-bg rotating circle */}
               <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                className="absolute inset-4 rounded-full border border-white/20 border-dashed"
              />

              {/* Central Floating Element */}
              <motion.div
                animate={{ y: [-12, 12, -12] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                className="relative w-72 h-72 rounded-full bg-gradient-to-tr from-white/10 to-white/30 backdrop-blur-lg flex items-center justify-center border border-white/40 shadow-[0_0_60px_-15px_rgba(255,255,255,0.4)]"
              >
                <CardGiftcardOutlinedIcon sx={{ fontSize: 140, color: "white" }} className="drop-shadow-2xl" />
              </motion.div>

              {/* Orbiting Elements */}
              <motion.div
                animate={{ y: [0, -15, 0], x: [0, 5, 0], rotate: [12, 15, 12] }}
                transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
                className="absolute -top-4 -right-2 w-28 h-28 rounded-3xl bg-white/10 backdrop-blur-xl border border-white/30 shadow-2xl flex items-center justify-center z-20"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-amber-300/20 to-transparent rounded-3xl pointer-events-none" />
                <FavoriteBorderOutlinedIcon sx={{ fontSize: 48, color: "white" }} className="drop-shadow-lg" />
              </motion.div>
              
              <motion.div
                animate={{ y: [0, 15, 0], x: [0, -5, 0], rotate: [-6, -4, -6] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}
                className="absolute -bottom-6 -left-8 w-32 h-32 rounded-full bg-white/10 backdrop-blur-xl border border-white/30 shadow-2xl flex flex-col items-center justify-center z-20"
              >
                 <div className="absolute inset-0 bg-gradient-to-br from-purple-400/20 to-transparent rounded-full pointer-events-none" />
                <span className="text-white font-black text-4xl drop-shadow-md tracking-tighter">50+</span>
                <span className="text-white/90 text-[10px] font-bold tracking-widest uppercase mt-1">Brands</span>
              </motion.div>

              <motion.div
                animate={{ y: [0, -10, 0], x: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 2.5 }}
                className="absolute top-1/2 -right-12 w-20 h-20 rounded-full bg-white/10 backdrop-blur-md border border-white/20 shadow-xl flex items-center justify-center z-10"
              >
                <FiberManualRecordIcon sx={{ fontSize: 24, color: "white" }} className="drop-shadow-lg" />
              </motion.div>

            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}
